<script>
  // ===== PROPS =====
  export let open = false;
  export let mode = 'bottom'; // bottom | anchor | center
  export let anchorRect = null;

  // 👇 optional size (override từ ngoài)
  export let width = null; // '420px' | '90%' | '80vw'
  export let height = null; // '600px' | '80vh'

  // ===== CONST =====
  const GAP = 8;
  const MARGIN = 8;

  // ===== DEFAULT SIZE THEO MODE =====
  const DEFAULT_SIZE = {
    anchor: {
      width: '200px',
      height: 'auto'
    },
    center: {
      width: '420px',
      height: 'auto'
    },
    bottom: {
      width: '100%',
      height: 'auto'
    }
  };

  let position = { top: 0, left: 0 };

  function close() {
    open = false;
  }

  // ===== BUILD STYLE STRING =====
  function sizeStyle() {
    const def = DEFAULT_SIZE[mode] ?? {};

    const w = width ?? def.width;
    const h = height ?? def.height;

    let style = '';
    if (w) style += `width:${w};`;
    if (h) style += `height:${h};`;

    return style;
  }

  // ===== CALC POSITION FOR ANCHOR =====
  function calcPosition() {
    if (!anchorRect) return { top: 0, left: 0 };

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const popupWidth = parseInt((width ?? DEFAULT_SIZE.anchor.width).toString()) || 200;

    let left = anchorRect.left + anchorRect.width / 2 - popupWidth / 2;

    left = Math.max(MARGIN, left);
    left = Math.min(left, vw - popupWidth - MARGIN);

    const spaceBelow = vh - anchorRect.bottom;
    const spaceAbove = anchorRect.top;

    let top;
    if (spaceBelow >= GAP + 100) {
      top = anchorRect.bottom + GAP;
    } else if (spaceAbove >= GAP + 100) {
      top = anchorRect.top - GAP - 100;
    } else {
      top = Math.max(MARGIN, vh - 100 - MARGIN);
    }

    return { top, left };
  }

  // ===== REACTIVE POSITION =====
  $: if (open && mode === 'anchor' && anchorRect) {
    position = calcPosition();
  }
</script>

  {#if open}
    <div class="popup-layer fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <!-- OVERLAY -->
      <div class="popup-overlay absolute inset-0 bg-black/40" on:click={close}></div>

    {#if mode === 'bottom'}
      <!-- ===== BOTTOM SHEET ===== -->
      <div
        class="absolute bottom-0 left-0 right-0
               bg-white rounded-t-2xl
               p-4 pb-6
               max-h-[92dvh]
               overflow-hidden
               animate-slide-up"
        style={sizeStyle()}>
        <slot />
      </div>
    {:else if mode === 'anchor' && anchorRect}
      <!-- ===== ANCHOR POPUP ===== -->
      <div
        class="absolute bg-white rounded-xl shadow-lg
               p-3 animate-scale-in"
        style="
          top:{position.top}px;
          left:{position.left}px;
          {sizeStyle()}
        ">
        <slot />
      </div>
    {:else if mode === 'center'}
      <!-- ===== CENTER MODAL ===== -->
      <div
        class="popup-center-shell bg-white rounded-2xl shadow-xl
               animate-scale-in-center"
        style={sizeStyle()}>
        <slot />
      </div>
    {/if}
  </div>
{/if}

<style>
  @keyframes slide-up {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  @keyframes scale-in {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes center-pop-in {
    from {
      transform: scale(0.985);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes overlay-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .animate-slide-up {
    animation: slide-up 0.25s ease-out;
  }

  .animate-scale-in {
    animation: scale-in 0.15s ease-out;
  }

  .animate-scale-in-center {
    animation: center-pop-in 120ms cubic-bezier(0.2, 0.8, 0.2, 1);
    transform-origin: center center;
    will-change: transform, opacity;
  }

  .popup-layer {
    contain: layout paint;
  }

  .popup-overlay {
    animation: overlay-fade-in 100ms ease-out;
    will-change: opacity;
  }

  .popup-center-shell {
    position: relative;
    display: flex;
    flex-direction: column;
    width: min(95vw, 72rem);
    max-width: 95vw;
    max-height: calc(100dvh - 1rem);
    overflow: hidden;
    min-height: 0;
    transform: translateZ(0);
    backface-visibility: hidden;
  }
</style>
